// Printable Purchase Order — A4 layout, auto-triggers window.print() on load.
// Staff saves as PDF (or prints) and emails the supplier directly.
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL;

export default function PurchaseOrderPrint() {
  const { poId } = useParams();
  const [po, setPo] = useState(null);
  const [supplier, setSupplier] = useState(null);
  const [site, setSite] = useState(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    const token = localStorage.getItem('uf_token') || localStorage.getItem('token') || localStorage.getItem('epos_token');
    const headers = { Authorization: `Bearer ${token}` };
    (async () => {
      try {
        const poRes = await fetch(`${API}/api/purchasing/purchase-orders/${poId}`, { headers });
        if (!poRes.ok) { setErr((await poRes.json()).detail || 'PO not found'); return; }
        const poData = await poRes.json();
        setPo(poData);
        const [sRes, siteRes] = await Promise.all([
          fetch(`${API}/api/purchasing/suppliers/${poData.supplier_id}`, { headers }),
          fetch(`${API}/api/sites`, { headers }),
        ]);
        if (sRes.ok) setSupplier(await sRes.json());
        if (siteRes.ok) {
          const sites = await siteRes.json();
          const list = sites.items || sites || [];
          setSite(list.find(x => x.id === poData.site_id));
        }
      } catch (e) {
        setErr('Could not load PO');
      }
    })();
  }, [poId]);

  useEffect(() => {
    if (po) {
      const t = setTimeout(() => window.print(), 500);
      return () => clearTimeout(t);
    }
  }, [po]);

  if (err) return <div style={{ padding: 40 }}>{err}</div>;
  if (!po) return <div style={{ padding: 40 }}>Loading PO…</div>;

  const date = po.created_at ? new Date(po.created_at).toLocaleDateString('en-GB') : '';
  const sent = po.sent_at ? new Date(po.sent_at).toLocaleDateString('en-GB') : null;

  return (
    <div className="po-root">
      <style>{`
        @page { size: A4; margin: 14mm 12mm; }
        @media print {
          html, body { background: #fff !important; }
          .no-print { display: none !important; }
          .po-root { box-shadow: none !important; padding: 0 !important; }
        }
        html, body {
          background: #eee;
          font-family: -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
          color: #111; margin: 0; padding: 0;
        }
        .po-root {
          max-width: 820px;
          margin: 20px auto;
          background: #fff;
          padding: 40px 48px;
          box-shadow: 0 2px 12px rgba(0,0,0,0.15);
          font-size: 12.5px;
          line-height: 1.45;
        }
        .no-print {
          max-width: 820px; margin: 12px auto 0;
          text-align: right;
        }
        .no-print button {
          padding: 8px 16px; margin-left: 8px;
          border: 1px solid #333; background: #fff;
          cursor: pointer; font-size: 13px;
        }
        .header {
          display: flex; justify-content: space-between; align-items: flex-start;
          border-bottom: 3px solid #E31837;
          padding-bottom: 14px; margin-bottom: 24px;
        }
        .brand { font-size: 26px; font-weight: 900; letter-spacing: -0.02em; }
        .brand .u { color: #111; }
        .brand .f { color: #E31837; }
        .brand .sub { font-size: 9px; letter-spacing: 0.25em; color: #666;
          text-transform: uppercase; margin-top: 4px; font-weight: 500; }
        .doc-title { text-align: right; }
        .doc-title h1 { margin: 0; font-size: 28px; letter-spacing: 0.04em;
          color: #E31837; font-weight: 800; }
        .doc-title .po-num { font-family: "SF Mono", Menlo, Consolas, monospace;
          font-size: 14px; font-weight: 600; color: #111; margin-top: 4px; }

        .blocks { display: grid; grid-template-columns: 1fr 1fr; gap: 30px; margin-bottom: 24px; }
        .block { }
        .block .label { font-size: 10px; text-transform: uppercase;
          letter-spacing: 0.1em; color: #888; font-weight: 700; margin-bottom: 6px; }
        .block .name { font-weight: 700; font-size: 14px; margin-bottom: 2px; }
        .block .line { color: #333; }

        .meta {
          display: grid; grid-template-columns: repeat(4, 1fr);
          gap: 20px; margin-bottom: 28px;
          padding: 12px 16px; background: #f7f7f7; border: 1px solid #e5e5e5;
        }
        .meta .label { font-size: 9px; text-transform: uppercase;
          letter-spacing: 0.1em; color: #888; font-weight: 700; margin-bottom: 3px; }
        .meta .value { font-size: 13px; font-weight: 600; color: #111; }

        table.lines { width: 100%; border-collapse: collapse; margin-bottom: 24px; }
        table.lines th {
          text-align: left; padding: 8px 10px;
          background: #111; color: #fff; font-size: 10px;
          text-transform: uppercase; letter-spacing: 0.08em;
        }
        table.lines th.r, table.lines td.r { text-align: right; }
        table.lines td {
          padding: 10px; border-bottom: 1px solid #ececec;
          vertical-align: top;
        }
        table.lines td .desc { color: #666; font-size: 11px; margin-top: 2px; }

        .totals { display: flex; justify-content: flex-end; }
        .totals-box { min-width: 240px; }
        .totals-box .row { display: flex; justify-content: space-between;
          padding: 6px 0; font-size: 13px; }
        .totals-box .row.grand {
          border-top: 2px solid #111; margin-top: 4px; padding-top: 10px;
          font-size: 16px; font-weight: 800;
        }
        .totals-box .row.grand .amt { color: #E31837; }

        .notes { margin-top: 28px; padding: 14px 16px;
          background: #fafafa; border-left: 3px solid #E31837; }
        .notes .label { font-size: 10px; text-transform: uppercase;
          letter-spacing: 0.1em; color: #888; font-weight: 700; margin-bottom: 4px; }

        .terms { margin-top: 28px; font-size: 10px; color: #666; line-height: 1.5;
          padding-top: 14px; border-top: 1px solid #ddd; }
        .terms b { color: #111; }

        .signature { margin-top: 40px;
          display: grid; grid-template-columns: 1fr 1fr; gap: 60px; }
        .signature .sig {
          border-top: 1px solid #333; padding-top: 6px;
          font-size: 10px; color: #666; text-transform: uppercase;
          letter-spacing: 0.1em;
        }

        .footer { margin-top: 30px; text-align: center; font-size: 10px;
          color: #999; }
      `}</style>

      <div className="no-print">
        <button onClick={() => window.print()}>Save PDF / Print</button>
        <button onClick={() => window.close()}>Close</button>
      </div>

      <div className="header">
        <div>
          <div className="brand"><span className="u">ULTRA</span><span className="f">FLEX</span></div>
          <div className="brand sub">UK GYM GROUP</div>
        </div>
        <div className="doc-title">
          <h1>PURCHASE ORDER</h1>
          <div className="po-num">{po.po_number}</div>
        </div>
      </div>

      <div className="blocks">
        <div className="block">
          <div className="label">Supplier</div>
          <div className="name">{supplier?.name || po.supplier_name}</div>
          {supplier?.contact_name && <div className="line">Attn: {supplier.contact_name}</div>}
          {supplier?.address_line_1 && <div className="line">{supplier.address_line_1}</div>}
          {supplier?.address_line_2 && <div className="line">{supplier.address_line_2}</div>}
          {(supplier?.city || supplier?.postcode) && <div className="line">{supplier?.city} {supplier?.postcode}</div>}
          {supplier?.country && <div className="line">{supplier.country}</div>}
          {supplier?.email && <div className="line" style={{ marginTop: 6 }}>{supplier.email}</div>}
          {supplier?.phone && <div className="line">{supplier.phone}</div>}
          {supplier?.vat_number && <div className="line" style={{ marginTop: 6, fontSize: 11, color: '#666' }}>VAT: {supplier.vat_number}</div>}
          {supplier?.account_number && <div className="line" style={{ fontSize: 11, color: '#666' }}>Account #: {supplier.account_number}</div>}
        </div>

        <div className="block">
          <div className="label">Deliver To</div>
          <div className="name">UltraFlex {site?.name?.replace('UltraFlex ', '') || po.site_name}</div>
          {site?.address_line_1 && <div className="line">{site.address_line_1}</div>}
          {site?.address_line_2 && <div className="line">{site.address_line_2}</div>}
          {(site?.city || site?.postcode) && <div className="line">{site?.city} {site?.postcode}</div>}
          {site?.phone && <div className="line" style={{ marginTop: 6 }}>{site.phone}</div>}
        </div>
      </div>

      <div className="meta">
        <div><div className="label">PO Date</div><div className="value">{date}</div></div>
        <div><div className="label">Expected</div><div className="value">{po.expected_date ? new Date(po.expected_date).toLocaleDateString('en-GB') : '—'}</div></div>
        <div><div className="label">Payment Terms</div><div className="value">{supplier?.payment_terms || 'Net 30'}</div></div>
        <div><div className="label">Currency</div><div className="value">GBP (£)</div></div>
      </div>

      <table className="lines">
        <thead>
          <tr>
            <th style={{ width: 30 }}>#</th>
            <th>Description</th>
            <th className="r" style={{ width: 70 }}>Qty</th>
            <th className="r" style={{ width: 90 }}>Unit Cost</th>
            <th className="r" style={{ width: 90 }}>Line Total</th>
          </tr>
        </thead>
        <tbody>
          {po.lines?.map((l, i) => (
            <tr key={l.id}>
              <td>{i + 1}</td>
              <td>
                <div><b>{l.product_name}</b></div>
                {l.product_sku && <div className="desc">SKU: {l.product_sku}</div>}
                {l.description && <div className="desc">{l.description}</div>}
              </td>
              <td className="r">{l.qty_ordered}</td>
              <td className="r">£{l.unit_cost?.toFixed(2)}</td>
              <td className="r">£{l.line_total?.toFixed(2)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="totals">
        <div className="totals-box">
          <div className="row"><span>Subtotal</span><span>£{po.subtotal?.toFixed(2)}</span></div>
          <div className="row"><span>VAT</span><span>£{po.vat_total?.toFixed(2) || '0.00'}</span></div>
          <div className="row grand"><span>TOTAL DUE</span><span className="amt">£{po.total_cost?.toFixed(2)}</span></div>
        </div>
      </div>

      {po.notes && (
        <div className="notes">
          <div className="label">Notes</div>
          <div>{po.notes}</div>
        </div>
      )}

      <div className="terms">
        <b>Terms of purchase:</b> Please reference the PO number <b>{po.po_number}</b> on all shipping
        documentation, invoices and correspondence. Goods must match the specification exactly;
        discrepancies will be flagged and may result in rejected delivery. Invoices without this PO
        number may be returned. Payment terms: {supplier?.payment_terms || 'Net 30'} from date of
        invoice, subject to goods received in good order.
      </div>

      <div className="signature">
        <div className="sig">Authorised by (UltraFlex)</div>
        <div className="sig">Accepted by (Supplier)</div>
      </div>

      <div className="footer">
        UltraFlex UK Gym Group · Purchase Order · Page 1 of 1
        {sent && <> · Issued {sent}</>}
      </div>
    </div>
  );
}
